// The session's state, and the few things every verb needs.
//
// **What `useBoardInteraction` held in React state, as fields.** The hooks
// kept a dozen `useState`s, three refs that existed only because React
// batches renders, and a reducer chosen for setter identity; a plain object
// needs none of that. Every verb mutates these fields and ends with
// `changed()`, which is the whole of the subscription protocol — the client's
// `useSyncExternalStore` reads `version` and rebuilds its view when it moves.
//
// **The live board, deliberately** (§29 U4). What is *drawn* while the player
// looks back is the viewed board; the session reasons about the live one and
// refuses every action instead. `send` is the one door a player's command
// goes through, and it is where that refusal is said.

import type { Cue, Focus, Square } from "src/api";
import type { GameSnapshot, GameStore } from "src/engine/game/GameStore";
import { seizedBy } from "src/engine/rules/campaign/scepter";
import { playableMoves } from "src/engine/rules/clock";
import type { Command, GameState } from "src/engine/rules/commands";
import type { Move } from "src/engine/rules/move";
import type { Color, Direction } from "src/shared/board";
import type { Carry } from "./carry";
import { NOTHING_REFUSED, type Refusal, REFUSAL, sayRefusal } from "./refusals";
import { canAct } from "./turn";
import type { Aiming, Forging, Pending, Placing } from "./types";

const NO_MOVES: Move[] = [];

/**
 * Where a command goes, and which seat this client holds.
 *
 * In a room the command goes to the server and the seat is the room's; alone
 * it goes to the store and there is no seat, so the side to move is whoever is
 * at the keyboard. Attached late, because the room is joined after the game
 * object exists.
 */
export interface SessionTransport {
  dispatch(command: Command): boolean;
  /** The seat this client sits in, or null for hot-seat. */
  seat(): Color | null;
}

export interface SessionEmit {
  cue(cue: Cue): void;
  focus(focus: Focus): void;
}

export class SessionCore {
  // --- what the board is doing -------------------------------------------
  selected?: number;
  /** A click that needs a direction before it becomes a command. */
  pending?: Pending;
  refused: Refusal = NOTHING_REFUSED;
  /** First square of a run, waiting for its far end. */
  lineAnchor?: number;
  /** The square the pointer is over — tracked only while an anchored run wants it. */
  hovered?: number;
  /** A forge waiting on a square. */
  forging?: Forging;
  /** Which way new pawns are pointed; null is "you decide" and is omitted from the command. */
  mintFacing: Direction | null = null;
  /** A structure token waiting for a square (§26 F6). */
  placing?: Placing;
  /** A targeted card or a power waiting for the board to be clicked. */
  aiming?: Aiming;
  /** How many times the same square has been clicked in a row: a repeat cycles the layers on it. */
  cycle?: { square: number; index: number };
  /** A freehand run being drawn, in order. Was state *and* a ref: pointer moves outran React. */
  path: number[] = [];
  /** What a drag is holding, and where the pointer is. */
  carry?: { what: Carry; at: { x: number; y: number } };
  /** The Discard tool is over a building, so the sweep has ended. */
  over = false;
  /** How much of `history` the selection has already followed. */
  seenMoves: number;

  // --- what the client tells it -----------------------------------------
  /** Is the Build tool up? Laying track on bare ground is the one click that does not infer from context. */
  building = false;
  /** The one side this client may play, null for hot-seat, undefined to ask the transport. */
  seatSide: Color | null | undefined;

  private version = 0;
  private listeners = new Set<() => void>();
  /** Alone: straight to the store, and no seat. */
  private readonly local: SessionTransport;
  private transport: SessionTransport;

  constructor(
    readonly store: GameStore,
    private readonly emit: SessionEmit
  ) {
    this.seenMoves = store.getSnapshot().state.history.length;
    this.local = { dispatch: (command) => store.dispatch(command), seat: () => null };
    this.transport = this.local;
  }

  attach(transport: SessionTransport | null): void {
    this.transport = transport ?? this.local;
    this.changed();
  }

  get snapshot(): GameSnapshot {
    return this.store.getSnapshot();
  }

  /** The live board — never the viewed one. */
  get state(): GameState {
    return this.store.getSnapshot().state;
  }

  /** Looking at an earlier position, so nothing may be played (§29 U4). */
  get viewing(): boolean {
    return this.store.getSnapshot().history.view !== null;
  }

  /**
   * Whose parts a click builds: the seat you occupy, not the side to move —
   * reading `position.turn` would build your opponent's lanes on their turn.
   * Hot-seat has no seat, and there the player at the keyboard *is* the side
   * to move.
   */
  get localSide(): Color {
    if (this.seatSide !== undefined && this.seatSide !== null) {
      // A turn this seat has seized (§31 A4) is played as the side on the clock.
      return seizedBy(this.state) === this.seatSide ? this.state.position.turn : this.seatSide;
    }
    const seat = this.transport.seat();
    if (seat !== null) return seat;
    return this.state.position.turn;
  }

  /** May this client move a piece at all right now? `canAct`, so the chrome cannot disagree. */
  get controlsTurn(): boolean {
    return canAct(this.state, this.localSide, this.viewing);
  }

  /**
   * Every legal move on the live board — **none once the turn's moves are
   * spent or a forge has closed them** (§31 T2, T4), so nothing lights a
   * square the reducer would refuse. Memoised on the position; shared.
   */
  get moves(): Move[] {
    const moves = playableMoves(this.state);
    return moves.length > 0 ? moves : NO_MOVES;
  }

  /**
   * The one door a player's command goes through, refused while looking back.
   *
   * Every armed card, belt, chip, upgrade and policy control dispatches
   * through here, so a surface added later cannot be the one that quietly did
   * nothing on an old board.
   */
  send(command: Command): boolean {
    if (!this.viewing) {
      const sent = this.transport.dispatch(command);
      this.changed();
      return sent;
    }
    this.setRefusal(REFUSAL["viewing-history"]);
    return false;
  }

  /** The refusal *is* the sentence, or null to clear it. A repeat bumps the count; clearing does not. */
  setRefusal(text: string | null): void {
    const next = sayRefusal(this.refused, text);
    if (next === this.refused) return;
    this.refused = next;
    this.changed();
  }

  cue(cue: Cue): void {
    this.emit.cue(cue);
  }

  /** Flash a square to show it just did something. */
  pulse(square: number): void {
    this.emit.focus({ kind: "pulse", square: square as Square });
  }

  /** Something the view is built from moved. */
  changed(): void {
    this.version++;
    for (const fn of this.listeners) fn();
  }

  get revision(): number {
    return this.version;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
}
